import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "../api";
import "./StripeCallbackPage.css";

type Status = "loading" | "success" | "error" | "incomplete";

const StripeCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>("loading");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    const nonce = searchParams.get("nonce");
    const token = searchParams.get("token");

    if (!nonce && !token) {
      setErrorMsg("Callback parameter is missing (nonce/token).");
      setStatus("error");
      return;
    }

    const run = async () => {
      try {
        try { await api.get("/csrf-token"); } catch {}

        const q = new URLSearchParams();
        if (nonce) q.set("nonce", nonce);
        else if (token) q.set("token", token);

        const { data } = await api.post(`/auth/complete-registration?${q.toString()}`);

        if (data?.msg === "registration_complete") {
          setStatus("success");
        } else if (data?.msg === "stripe_incomplete") {
          setStatus("incomplete");
        } else {
          setErrorMsg(data?.msg || "Unexpected server response");
          setStatus("error");
        }
      } catch (err: any) {
        const msg = err?.response?.data?.msg;
        if (msg === "stripe_incomplete") {
          setStatus("incomplete");
        } else {
          setErrorMsg(msg || "Failed to finalize registration");
          setStatus("error");
        }
      }
    };

    run();
  }, [searchParams]);

  if (status === "loading") {
    return <p className="stripe-status">Finalizing registration…</p>;
  }

  if (status === "incomplete") {
    return (
      <div className="stripe-callback-container">
        <div className="stripe-box">
          <h2 className="stripe-incomplete-title">Stripe Onboarding Incomplete 😕</h2>
          <p className="stripe-incomplete-message">
            You have not completed the Stripe setup. Please return to finish the process:
          </p>
          <button className="stripe-return-button" onClick={() => navigate("/login")}>
            Return and Complete Stripe Onboarding
          </button>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="stripe-callback-container">
        <div className="stripe-box">
          <h2 className="stripe-error">Something went wrong.</h2>
          <p className="stripe-error-msg">{errorMsg}</p>
          <Link className="stripe-link" to="/register">Try Again</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="stripe-callback-container">
      <div className="stripe-box">
        <h1 className="stripe-title">Registration Complete 🎉</h1>
        <p className="stripe-message">Please check your email to activate your account.</p>
        <Link className="stripe-link" to="/login">Log In</Link>
      </div>
    </div>
  );
};

export default StripeCallbackPage;
